/**
 * PesticidesCatalogPage — пользовательский справочник СЗР (AK-385).
 *
 * Read-only, любой аутентифицированный пользователь. Переключатель трёх видов
 * поиска (Tabs):
 *   1. По названию → список → карточка препарата (регламенты + аналоги).
 *   2. По действующему веществу → список препаратов с этим д.в.
 *   3. Подбор: культура + вредный объект + производитель (+«только активные»),
 *      выдача сгруппирована по подписи д.в.
 *
 * Источник данных — /api/v1/reference/pesticides (react-query hooks). Все
 * состояния default/loading/empty/error покрыты. Адаптив: широкие таблицы
 * скроллят в своём контейнере; без 100vh на контейнерах.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, FlaskConical } from 'lucide-react';
import { BrandIcon } from '@/components/ui/brand-icon';
import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  reverseSearchReady,
  useProduct,
  useProducts,
  useProductsBySubstance,
  useReverseSearch,
  useSubstancesSearch,
} from '@/lib/pesticides/queries';
import type {
  EntityOption,
} from './EntityPicker';
import { EntityPicker } from './EntityPicker';
import { PesticideFreshnessPlaque } from '@/components/pesticides/PesticideFreshnessPlaque';
import { ProductCardSkeleton, ProductCardView } from './ProductCardView';
import { fmtRate, statusLabel, substancesLine } from './parts';
import type { ProductListItem, ReverseSearchQuery, SubstanceItem } from '@/lib/pesticides/types';

type Mode = 'name' | 'substance' | 'reverse';

function useDebounced(value: string, delay = 300): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function ErrorRow({ error }: { error: Error | null }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 text-sm text-destructive">
      <AlertCircle className="h-4 w-4" />
      {error?.message ?? 'Не удалось загрузить данные'}
    </div>
  );
}

// ─── mode 1: by name ───

function ProductListRow({
  item,
  active,
  onClick,
}: {
  item: ProductListItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex w-full flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors ' +
        (active
          ? 'border-primary bg-primary/5'
          : 'border-border hover:bg-accent')
      }
    >
      <div className="flex items-center gap-2">
        <span className="font-medium text-foreground">{item.name}</span>
        {item.formulation && <Badge variant="outline">{item.formulation}</Badge>}
        <Badge variant={item.status === 'active' ? 'primary' : 'secondary'}>
          {statusLabel(item.status)}
        </Badge>
      </div>
      <span className="text-xs text-muted-foreground">
        {substancesLine(item.substances)}
      </span>
    </button>
  );
}

function ByNameMode() {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQ = useProducts({ q: debounced || undefined, status: 'active' });
  const cardQ = useProduct(selectedId);
  const items = listQ.data?.items ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
      <div>
        <div className="relative">
          <BrandIcon
            name="search"
            size={16}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по названию — напр. Эместо Квантум"
            className="pl-8"
          />
        </div>
        <Card className="mt-3 p-2">
          {listQ.isError && <ErrorRow error={listQ.error} />}
          {listQ.isLoading && (
            <div className="space-y-2 p-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {!listQ.isLoading && !listQ.isError && items.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              {debounced
                ? 'Препараты не найдены.'
                : 'Начните вводить название препарата.'}
            </div>
          )}
          <div className="space-y-2">
            {items.map((it) => (
              <ProductListRow
                key={it.id}
                item={it}
                active={it.id === selectedId}
                onClick={() => setSelectedId(it.id)}
              />
            ))}
          </div>
        </Card>
      </div>

      <div className="min-w-0">
        {!selectedId && (
          <Card className="flex flex-col items-center justify-center gap-2 p-12 text-center">
            <FlaskConical className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm font-medium text-foreground">
              Выберите препарат
            </div>
            <div className="max-w-sm text-xs text-muted-foreground">
              Слева — результаты поиска. Кликните препарат, чтобы увидеть шапку,
              регламенты применения и аналоги.
            </div>
          </Card>
        )}
        {selectedId && cardQ.isLoading && <ProductCardSkeleton />}
        {selectedId && cardQ.isError && (
          <Card className="p-5">
            <ErrorRow error={cardQ.error} />
          </Card>
        )}
        {selectedId && cardQ.data && (
          <ProductCardView product={cardQ.data} onOpen={setSelectedId} />
        )}
      </div>
    </div>
  );
}

// ─── mode 2: by substance ───

function BySubstanceMode() {
  const [q, setQ] = useState('');
  const debounced = useDebounced(q);
  const [selected, setSelected] = useState<SubstanceItem | null>(null);

  const substancesQ = useSubstancesSearch(debounced, debounced.length > 0);
  const productsQ = useProductsBySubstance(selected?.id ?? null, 'active');
  const substances = substancesQ.data?.items ?? [];
  const products = productsQ.data ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,320px)_1fr]">
      <div>
        <div className="relative">
          <BrandIcon
            name="search"
            size={16}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Действующее вещество — напр. Клотианидин"
            className="pl-8"
          />
        </div>
        <Card className="mt-3 p-2">
          {substancesQ.isError && <ErrorRow error={substancesQ.error} />}
          {substancesQ.isLoading && (
            <div className="space-y-2 p-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          )}
          {!debounced && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              Введите название действующего вещества.
            </div>
          )}
          {debounced && !substancesQ.isLoading && substances.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              Вещества не найдены.
            </div>
          )}
          <div className="space-y-1">
            {substances.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelected(s)}
                className={
                  'w-full rounded-md px-3 py-2 text-left text-sm transition-colors ' +
                  (s.id === selected?.id
                    ? 'bg-primary/5 font-medium text-foreground'
                    : 'text-foreground hover:bg-accent')
                }
              >
                {s.name_ru}
                {s.name_iso && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {s.name_iso}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="min-w-0">
        {!selected && (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            Выберите действующее вещество слева — покажем препараты с ним.
          </Card>
        )}
        {selected && (
          <Card>
            <div className="border-b border-border px-4 py-3">
              <h3 className="text-sm font-medium text-foreground">
                Препараты, содержащие «{selected.name_ru}»
                {products.length ? ` · найдено ${products.length}` : ''}
              </h3>
            </div>
            {productsQ.isError && <ErrorRow error={productsQ.error} />}
            {productsQ.isLoading && (
              <div className="space-y-2 p-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            )}
            {!productsQ.isLoading && !productsQ.isError && products.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Активных препаратов с этим веществом не найдено.
              </div>
            )}
            {products.length > 0 && (
              <div className="overflow-x-auto">
                <Table className="min-w-[560px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Препарат</TableHead>
                      <TableHead className="w-[240px]">Содержание д.в.</TableHead>
                      <TableHead className="w-[90px]">Форма</TableHead>
                      <TableHead className="w-[100px]">Статус</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-foreground">
                          {p.name}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {substancesLine(p.substances)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.formulation ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {statusLabel(p.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── mode 3: reverse (culture + harmful object + registrant) ───

const REF = {
  crops: { path: '/api/v1/reference/crops', translated: true },
  pests: { path: '/api/v1/reference/pests', translated: true },
  diseases: { path: '/api/v1/reference/diseases', translated: true },
  weeds: { path: '/api/v1/reference/weeds', translated: true },
  registrants: { path: '/api/v1/reference/pesticides/registrants', translated: false },
} as const;

type HarmfulKind = 'pest' | 'disease' | 'weed';

function ReverseMode() {
  const [crop, setCrop] = useState<EntityOption | null>(null);
  const [harmfulKind, setHarmfulKind] = useState<HarmfulKind>('pest');
  const [harmful, setHarmful] = useState<EntityOption | null>(null);
  const [registrant, setRegistrant] = useState<EntityOption | null>(null);
  const [onlyActive, setOnlyActive] = useState(true);

  const query: ReverseSearchQuery = useMemo(
    () => ({
      crop_id: crop?.id,
      object_kind: harmful ? harmfulKind : undefined,
      object_ref_id: harmful?.id,
      registrant_id: registrant?.id,
      status: onlyActive ? 'active' : null,
    }),
    [crop, harmful, harmfulKind, registrant, onlyActive],
  );

  const q = useReverseSearch(query);
  const ready = reverseSearchReady(query);
  const groups = q.data ?? [];
  const totalProducts = groups.reduce((n, g) => n + g.products.length, 0);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-[1fr_1.7fr_1fr_auto]">
          <div>
            <Label className="mb-1.5 block text-xs">Культура</Label>
            <EntityPicker
              source={REF.crops}
              value={crop}
              onChange={setCrop}
              placeholder="Напр. Картофель"
            />
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <Label className="mb-1.5 block text-xs">Вредный объект</Label>
            <div className="flex gap-2">
              <select
                value={harmfulKind}
                onChange={(e) => {
                  setHarmfulKind(e.target.value as HarmfulKind);
                  setHarmful(null);
                }}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                aria-label="Тип вредного объекта"
              >
                <option value="pest">Вредитель</option>
                <option value="disease">Болезнь</option>
                <option value="weed">Сорняк</option>
              </select>
              <div className="min-w-0 flex-1">
                <EntityPicker
                  source={
                    harmfulKind === 'pest'
                      ? REF.pests
                      : harmfulKind === 'disease'
                        ? REF.diseases
                        : REF.weeds
                  }
                  value={harmful}
                  onChange={setHarmful}
                  placeholder="Напр. Колорадский жук"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Производитель</Label>
            <EntityPicker
              source={REF.registrants}
              value={registrant}
              onChange={setRegistrant}
              placeholder="Напр. Байер"
            />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <Switch
              id="only-active"
              checked={onlyActive}
              onCheckedChange={setOnlyActive}
            />
            <Label htmlFor="only-active" className="text-sm">
              Только активные
            </Label>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Задайте хотя бы один фильтр (культура, вредный объект или
          производитель). Выдача сгруппирована по составу д.в.
        </p>
      </Card>

      {!ready && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Укажите фильтры выше, чтобы подобрать препараты.
        </Card>
      )}

      {ready && q.isError && (
        <Card className="p-4">
          <ErrorRow error={q.error} />
        </Card>
      )}

      {ready && q.isLoading && (
        <Card className="space-y-2 p-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      )}

      {ready && !q.isLoading && !q.isError && groups.length === 0 && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          По заданным фильтрам препараты не найдены.
        </Card>
      )}

      {ready && groups.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Сгруппировано по д.в. · найдено {totalProducts}
            {registrant ? ` (производитель: ${registrant.name})` : ''}
          </div>
          {groups.map((g) => (
            <Card key={g.substance_signature.join('+') || 'no-sig'}>
              <div className="border-b border-border px-4 py-2 text-sm font-medium text-foreground">
                {g.substance_signature.length
                  ? g.substance_signature.join(' + ')
                  : 'Состав не указан'}
              </div>
              <div className="overflow-x-auto">
                <Table className="min-w-[620px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Препарат</TableHead>
                      <TableHead className="w-[150px]">Норма</TableHead>
                      <TableHead className="w-[130px]">Срок ожидания</TableHead>
                      <TableHead className="w-[160px]">Заявитель</TableHead>
                      <TableHead className="w-[100px]">Статус</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.products.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium text-foreground">
                          {p.name}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fmtRate(p.rate_min, p.rate_max, p.rate_unit)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.waiting_days ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.registrant ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {statusLabel(p.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function PesticidesCatalogPage() {
  const [mode, setMode] = useState<Mode>('name');

  return (
    <AppShell>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Справочник пестицидов
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Каталог зарегистрированных СЗР · Госкаталог Минсельхоза РФ.
            Регламенты — строго по колонкам каталога.
          </p>
          {/* Свежесть данных: «данные на {edition} · синхронизировано {date}» —
              чтобы юзер не спутал «давно не обновлялись» с «препарат исключён». */}
          <PesticideFreshnessPlaque className="mt-1.5" />
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList>
            <TabsTrigger value="name">По названию</TabsTrigger>
            <TabsTrigger value="substance">По действующему веществу</TabsTrigger>
            <TabsTrigger value="reverse">По культуре + объекту</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === 'name' && <ByNameMode />}
        {mode === 'substance' && <BySubstanceMode />}
        {mode === 'reverse' && <ReverseMode />}
      </div>
    </AppShell>
  );
}

export default PesticidesCatalogPage;
